import { GetTimeseriesRequest } from "../types/timeseries";
import { useMutation, useQuery } from "react-query";
import {
  getEmailConfirmationService,
  getResetPasswordConfirmationService,
  postForgotPasswordService,
  postLoginService,
  postLogoutService,
  postRefreshTokenService,
  postRegisterService,
  postResendActivationService,
  postResetPasswordService,
} from "../services/auth";
import { useAuthContext } from "./useAuthContext";
import { useToast } from "../Components/Provider/ToastProvider";

export type UseTimeseries = GetTimeseriesRequest;

const useAuth = ({
  email,
  token,
}: {
  email?: string;
  token?: string;
} = {}) => {
  const { login, logout, parsedToken } = useAuthContext();
  const { showToast } = useToast();

  const postLogin = useMutation(
    async (payload: { email: string; password: string }) => {
      return await postLoginService(payload);
    },
    {
      retry: false,
      onSuccess: (response) => {
        if (response) {
          login(response.data);
          showToast("success", "Login successful!");
        }
      },
      onError: () => {
        showToast("error", "Login failed");
      },
    }
  );

  const postRegister = useMutation(
    async (payload: {
      email: string;
      fullName: string;
      password: string;
      confirmPassword: string;
      affiliation: string;
      targetCountries: string;
    }) => {
      return await postRegisterService(payload);
    },
    {
      retry: false,
      onSuccess: async (response) => {
        if (response.success) {
          showToast(
            "success",
            "Invitation link sent. Please check your email to complete registration."
          );
        } else if (response?.errors) {
          if (response?.errors[0].code === "User.EmailExists")
            await postResendActivation.mutateAsync({ email: response?.email });
        }
      },
      onError: (err) => {
        console.log(err, "err");
        showToast("error", "Failed to send invitation. Please try again.");
      },
    }
  );

  const postResendActivation = useMutation(
    async (payload: { email: string }) => {
      return await postResendActivationService(payload);
    },
    {
      retry: false,
      onSuccess: (response) => {
        if (response.success) {
          showToast(
            "success",
            "Invitation link sent. Please check your email to complete registration."
          );
        }
      },
      onError: (err) => {
        console.log(err, "err");
        showToast("error", "Failed to send invitation. Please try again.");
      },
    }
  );

  const postLogout = useMutation(
    async () => {
      return await postLogoutService();
    },
    {
      retry: false,
      onSuccess: (res) => {
        logout();
        showToast("info", "You’ve been logged out.");
      },
      onError: () => {
        showToast("error", "Sign out failed. Try again.");
      },
    }
  );

  const postRefreshToken = useMutation(
    async () => {
      return await postRefreshTokenService({
        refreshToken: parsedToken?.refreshToken ?? "",
      });
    },
    {
      retry: false,
      onSuccess: (response) => {
        if (response) {
          login(response.data);
        }
      },
    }
  );

  const getEmailConfirmation = useQuery(
    ["emailConfirmation", email, token],
    async () => {
      return await getEmailConfirmationService({ email, token });
    },
    {
      refetchOnWindowFocus: false,
      refetchOnMount: false,
      retry: false,
      enabled: false,
      onError: () => {
        showToast(
          "error",
          "Invalid token. Please request a new account invitation to complete registration."
        );
      },
    }
  );

  const getResetPasswordConfirmation = useQuery(
    ["resetPasswordConfirmation", email, token],
    async () => {
      return await getResetPasswordConfirmationService({ email, token });
    },
    {
      refetchOnWindowFocus: false,
      refetchOnMount: false,
      retry: false,
      enabled: false,
      onError: () => {
        showToast(
          "error",
          "Invalid token. Please request a new reset invitations"
        );
      },
    }
  );

  const postForgotPassword = useMutation(
    async (payload: { email: string }) => {
      return await postForgotPasswordService(payload);
    },
    {
      retry: false,
      onSuccess: () => {
        showToast("success", "Reset link sent. Check your email to continue.");
      },
      onError: () => {
        showToast(
          "error",
          "We couldn’t send the reset link. Please try again."
        );
      },
    }
  );

  const postResetPassword = useMutation(
    async (payload: { email: string; token: string; newPassword: string }) => {
      return await postResetPasswordService(payload);
    },
    {
      retry: false,
      onSuccess: () => {
        showToast(
          "success",
          "Your password has been updated. You can now log in."
        );
      },
      onError: (error: any) => {
        if (error?.response?.status === 400) {
          showToast(
            "error",
            "This reset link has expired. Please request a new one."
          );
        } else {
          showToast(
            "error",
            "We couldn’t reset your password. Please try again."
          );
        }
      },
    }
  );

  return {
    postLogin,
    postRegister,
    postLogout,
    postForgotPassword,
    postRefreshToken,
    getEmailConfirmation,
    getResetPasswordConfirmation,
    postResetPassword,
  };
};

export default useAuth;
